/**
 * next/navigation's usePathname / useSearchParams over TanStack Router.
 *
 * Both must return null OUTSIDE a RouterProvider (jsdom component tests
 * render layout pieces with no router, and the call sites already handle
 * null). TanStack's own useLocation throws there, and hooks cannot be called
 * conditionally, so the location is read through useSyncExternalStore with a
 * no-op subscription when no router is present.
 */
import { useRouter as useTanStackRouter } from '@tanstack/react-router'
import { useMemo, useSyncExternalStore } from 'react'

interface LocationSource {
  subscribe: (event: 'onResolved', fn: () => void) => () => void
  state: { location: { pathname: string; searchStr: string } }
}

interface RenderedMatchesSource {
  stores: {
    matches: {
      get: () => { pathname: string }[]
      subscribe: (fn: () => void) => { unsubscribe: () => void }
    }
  }
}

function useLocationField(select: (location: { pathname: string; searchStr: string }) => string): string | null {
  const router = useTanStackRouter({ warn: false }) as unknown as LocationSource | undefined
  return useSyncExternalStore(
    (onStoreChange) => (router ? router.subscribe('onResolved', onStoreChange) : () => undefined),
    () => (router ? select(router.state.location) : null),
    () => (router ? select(router.state.location) : null),
  )
}

export function usePathname(): string | null {
  return useLocationField((location) => location.pathname)
}

export function useSearchParams(): URLSearchParams | null {
  const searchStr = useLocationField((location) => location.searchStr)
  return useMemo(() => (searchStr === null ? null : new URLSearchParams(searchStr)), [searchStr])
}

/**
 * The pathname of the page the <Outlet /> is rendering right now.
 *
 * usePathname() and useRouter().pathname move to the next URL the moment a
 * navigation starts, while the outlet keeps rendering the previous route until
 * the next one has loaded (every route component is code-split). A layout that
 * picks the frame around its outlet must pick it for the page on screen, so it
 * reads the committed leaf match instead (NONRESIDENTTAX-FA).
 */
export function useRenderedPathname(): string | null {
  const router = useTanStackRouter({ warn: false }) as unknown as RenderedMatchesSource | undefined
  const read = () => (router ? (router.stores.matches.get().at(-1)?.pathname ?? null) : null)
  return useSyncExternalStore(
    (onStoreChange) => {
      if (!router) return () => undefined
      const subscription = router.stores.matches.subscribe(onStoreChange)
      return () => subscription.unsubscribe()
    },
    read,
    read,
  )
}
